import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { SessionService } from './session.service';
import { v4 as uuidv4 } from 'uuid'; // Added import
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { timeout, retry } from 'rxjs/operators';
import Resources from '@constants/resourses';
import { DetectBrowserService } from './detect-browser.service';
import { UtilityService } from './utility.service';

export interface GoogleAnalyticsEvent {
  eventName: string;
  eventParams: Record<string, string>;
  uuid: string;
  email?: string;
  abVariant?: string;
  ipAddress?: string;
  browser?: {
    name: string;
    version: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  perseGAInitialized = false;
  sessionService = inject(SessionService);
  browserDetect = inject(DetectBrowserService);
  http = inject(HttpClient);
  queuedEvents: { eventName: string, eventData: Record<string, string> }[] = [];
  retryCount = 0;
  gaInitRetryTimer: any = null;
  MAX_RETRY_COUNT = 10;
  RETRY_DELAY_MS = 2000;
  uuid = ''; // Added uuid property

  constructor(
    private utilityService: UtilityService,
  ) {
    this.initGoogleAnalytics();
  }

  initGoogleAnalytics() {
    // Clear any existing timer to prevent multiple timers
    if (this.gaInitRetryTimer) {
      clearTimeout(this.gaInitRetryTimer);
      this.gaInitRetryTimer = null;
    }

    // Initialize or retrieve UUID from session storage
    const existingUuid = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.GoogleAnalyticsUuid);
    if (existingUuid) {
      this.uuid = existingUuid;
    } else {
      this.uuid = uuidv4();
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.GoogleAnalyticsUuid, this.uuid); // Store UUID in session storage
    }

    // window.gtag is set up by Tealium.
    // Once it is loaded, we configure perse GA Measurement ID to track the data
    if (typeof window !== 'undefined' && window['gtag']) {
      // window['gtag']('config', environment.gtagPerse);
      this.perseGAInitialized = true;
      this.retryCount = 0;

      // Process any queued events now that GA is initialized
      while (this.queuedEvents.length > 0) {
        const event = this.queuedEvents.shift();
        if (event) {
          this.track(event.eventData, event.eventName);
        }
      }
    } else {
      // Google Analytics is not loaded. Retry after delay
      if (this.retryCount < this.MAX_RETRY_COUNT) {
        this.retryCount++;
        this.gaInitRetryTimer = setTimeout(() => {
          this.initGoogleAnalytics();
        }, this.RETRY_DELAY_MS);
      } else {
        console.warn('Failed to initialize Google Analytics after maximum retry attempts. Using manual API tracking only.');
        // Mark as initialized to allow manual API tracking
        this.perseGAInitialized = true;
      }
    }
  }

  track(data, eventName = 'custom_track') {
    const prefixedEventName = 'perse_' + eventName;
    const browserDetected = this.browserDetect.getBrowser();

    if (this.perseGAInitialized) {
      // console.log('GA Event tracked::', prefixedEventName, data);
      const ipAddress = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.IpAddress) ?? 'UNKNOWN_IP';

      // window['gtag']('event', prefixedEventName, {
      //   // send_to ensures the event is sent only to perse GA
      //   // This prevents duplicate events being sent to Tealium
      //   send_to: environment.gtagPerse,
      //   uuid: this.uuid, // Add uuid to tracking data,
      //   abVariant: data.abVariant,
      //   ipAddress: ipAddress,
      //   ...data
      // });
      const req = {
        eventName: prefixedEventName,
        eventParams: data,
        abVariant: data.abVariant,
        ipAddress: ipAddress,
        browser: browserDetected,
        uuid: this.uuid
      };
      const triggerTime = new Date().getTime();
      this.manualAPITrack(req).subscribe({
        next: (res) => { },
        error: (err) => {
          const responseTime = (new Date().getTime() - triggerTime) / 1000;
          console.error('Error tracking event with manual API:', err);
          // Trigger an email failure alert if needed
          this.utilityService.sendAPIError('Google Analytics tracking failed', req, err, responseTime);
        }
      });
    } else {
      this.queuedEvents.push({
        eventName: eventName,
        eventData: data
      });
    }
  }

  manualAPITrack(event: GoogleAnalyticsEvent) {
    return this.http.post<{ success: boolean }>(`${environment.analytics_v1_url}/cron/ui-analytics`, event, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'e_api_key': environment.resource_identifier
      })
    }).pipe(
      timeout(30000),
      retry(1)
    );
  }
}